import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Zap, ChevronRight, Target, Shield, Brain, Clock, Cloud } from 'lucide-react';
import { GlassButton } from '../components/ui/glass-button';
import { Drone3D } from '../components/ui/Drone3D';
import drone3 from '../assets/drone3.png';
import drone4 from '../assets/drone4.png';
import drone5 from '../assets/drone5.png';
import drone6 from '../assets/drone6.png';
import drone7 from '../assets/drone7.png';
import logoImg from '../assets/logo.png';

import { ShaderAnimation } from '../components/ui/shader-animation';
import { SuccessSlider } from '../components/SuccessSlider';

gsap.registerPlugin(ScrollTrigger);

const SLIDES = [
  { src: drone3, label: 'Drone Soccer' },
  { src: drone4, label: 'Teknoloji' },
  { src: drone5, label: 'Heyecan' },
  { src: drone6, label: 'FPV Pilot' },
  { src: drone7, label: 'Arena' },
];

const FEATURES = [
  "Kevlar takviyeli koruyucu kafesler",
  "0.1ms gecikmeli görüntü aktarımı",
  "Yapay zeka destekli uçuş asistanı",
  "Ultra-dayanıklı karbon fiber gövde",
];

const FeaturedSection: React.FC = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);

  useEffect(() => {
    const t = setInterval(() => setCurrent(p => (p + 1) % SLIDES.length), 3500);
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => {
      clearInterval(t);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <section id="teknoloji-hiz" ref={ref} className="relative w-full overflow-hidden" style={{ minHeight: 300, background: '#ffffff' }}>



      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-16 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center"
        style={{ paddingTop: isMobile ? '60px' : '64px', paddingBottom: isMobile ? '60px' : '64px' }}>

        {/* RIGHT (Moved to Top on Mobile): Tilted Slider Stack */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          style={{ position: 'relative', height: 'clamp(200px, 35vw, 300px)' }}
          className="order-1 lg:order-2 w-full mt-4 lg:mt-0 lg:h-[340px]"
        >
          {/* Shadow card (behind, tilted) */}
          <div style={{ position: 'absolute', inset: 0, borderRadius: 20, background: 'rgba(230,81,0,0.18)', transform: 'rotate(4deg) scale(0.96)', top: 16, left: 8 }} />

          {/* Main image card (Slider) */}
          <motion.div
            style={{ y: imgY, position: 'absolute', inset: 0, borderRadius: 20, overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.5)', transform: 'rotate(-1.5deg)' }}
            whileHover={{ rotate: 0, transition: { duration: 0.4 } }}
          >
            {SLIDES.map((slide, i) => (
              <img
                key={slide.src}
                src={slide.src}
                alt={slide.label}
                loading="lazy"
                style={{
                  position: 'absolute', inset: 0, width: '100%', height: '100%',
                  objectFit: 'cover',
                  opacity: i === current ? 1 : 0,
                  transform: i === current ? 'scale(1)' : 'scale(1.05)',
                  transition: 'opacity 0.9s ease, transform 0.9s ease',
                  zIndex: i === current ? 1 : 0,
                }}
              />
            ))}

            {/* Overlays */}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, transparent 40%, rgba(15,45,94,0.8) 100%)', zIndex: 2 }} />

            {/* Corner HUD */}
            <div style={{ position: 'absolute', top: 16, left: 16, width: 32, height: 32, borderTop: '2px solid rgba(230,81,0,0.8)', borderLeft: '2px solid rgba(230,81,0,0.8)', zIndex: 3 }} />
            <div style={{ position: 'absolute', bottom: 16, right: 16, width: 32, height: 32, borderBottom: '2px solid rgba(230,81,0,0.8)', borderRight: '2px solid rgba(230,81,0,0.8)', zIndex: 3 }} />



            {/* Dots */}
            <div style={{ position: 'absolute', bottom: 20, right: 20, display: 'flex', gap: 6, zIndex: 4 }}>
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  style={{
                    height: 4, borderRadius: 9999, border: 'none', cursor: 'pointer',
                    width: i === current ? 18 : 4,
                    background: i === current ? '#e65100' : 'rgba(255,255,255,0.3)',
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </div>
          </motion.div>


        </motion.div>

        {/* LEFT: Content Side */}
        <div className="flex flex-col gap-4 lg:gap-10 order-2 lg:order-1">

          {/* Label pill */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(230,81,0,0.15)', border: '1px solid rgba(230,81,0,0.35)', borderRadius: 99, padding: '4px 12px', alignSelf: 'flex-start' }}
          >
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#e65100', display: 'block', animation: 'pulse 1.5s infinite' }} />
            <span style={{ color: '#e65100', fontSize: 9, fontWeight: 900, letterSpacing: '0.4em', textTransform: 'uppercase' }}>SAHAMIZ, GÖKYÜZÜ</span>
          </motion.div>

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h2 className="font-black font-headline uppercase tracking-tighter leading-[0.95] lg:leading-[1]" style={{ fontSize: 'clamp(1.2rem, 3.2vw, 2.4rem)' }}>
              <div className="mb-2">
                <span style={{ color: '#e65100', marginRight: '0.3em' }}>SATRANÇ KADAR</span>
                <span style={{ color: '#0f2d5e' }}>STRATEJİK</span>
              </div>
              <div>
                <span style={{ color: '#0f2d5e', marginRight: '0.3em' }}>FORMULA 1 KADAR</span>
                <span style={{ color: '#e65100' }}>HIZLI</span>
              </div>
            </h2>
          </motion.div>

          {/* Divider */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            style={{ height: 1, background: 'linear-gradient(to right, rgba(15,45,94,0.15), transparent)', transformOrigin: 'left' }}
          />

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            style={{ fontSize: isMobile ? '18px' : '1.1rem', lineHeight: 1.5, maxWidth: 440, fontWeight: 700 }}
          >
            <span style={{ color: '#e65100' }}>Drone Soccer</span>,
            <span style={{ color: '#0f2d5e' }}> dünyayı kasıp kavuran fenomen,</span>
          </motion.p>

          {/* Feature list */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.4 }} className="flex flex-col gap-4">
            {[
              { t: "Sahası gökyüzü olan ve", i: Cloud },
              { t: "Pratik zeka", i: Brain },
              { t: "Hızlı refleksler", i: Zap },
              { t: "Doğru zamanlama", i: Clock },
              { t: "Anlık kararlar", i: Target },
              { t: "Risk yönetimi", i: Shield },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.45 + idx * 0.08 }}
                style={{ display: 'flex', alignItems: 'center', gap: 12 }}
              >
                <div style={{ width: 22, height: 22, borderRadius: 5, background: 'rgba(230,81,0,0.18)', border: '1px solid rgba(230,81,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <item.i style={{ width: 11, height: 11, color: '#e65100' }} />
                </div>
                <span style={{ color: '#4b5563', fontSize: isMobile ? '17px' : '0.85rem', fontWeight: 600 }}>{item.t}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* Concluding text for the list */}
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            style={{ color: '#0f2d5e', fontSize: isMobile ? '17px' : '0.95rem', lineHeight: 1.6, marginTop: 16, maxWidth: 440, fontWeight: 700 }}
          >
            gerektiren bu spor <span style={{ color: '#e65100' }}>çocuklarımız ve gençlerimiz</span> için geleceğin kapılarını bugünden aralıyor.
          </motion.p>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="origin-left"
          >
            <GlassButton
              size={isMobile ? "sm" : "default"}
              contentClassName="group"
              onClick={() => navigate('/hakkimizda')}
            >
              HAKKIMIZDA <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </GlassButton>
          </motion.div>
        </div>

      </div>
    </section>
  );
};

// CSS Animasyonlu Bulut Bileşeni (Düzeltildi)
const AnimatedCloud = ({ top, delay, duration, opacity, scale }: { top: string, delay: number, duration: number, opacity: number, scale: number }) => (
  <motion.div
    initial={{ x: "-30vw" }}
    animate={{ x: "120vw" }}
    transition={{ repeat: Infinity, duration, ease: "linear", delay }}
    style={{ position: 'absolute', pointerEvents: 'none', top, opacity, transform: `scale(${scale})`, zIndex: 0 }}
  >
    <div style={{ position: 'relative', width: '280px', height: '80px', background: '#fff', borderRadius: '100px', filter: 'blur(16px)' }}>
      <div style={{ position: 'absolute', top: '-50px', left: '40px', width: '100px', height: '100px', background: '#fff', borderRadius: '50%' }} />
      <div style={{ position: 'absolute', top: '-80px', left: '100px', width: '140px', height: '140px', background: '#fff', borderRadius: '50%' }} />
      <div style={{ position: 'absolute', top: '-30px', left: '200px', width: '80px', height: '80px', background: '#fff', borderRadius: '50%' }} />
    </div>
  </motion.div>
);

// CSS-only background pattern (replacing heavy WebGL background)


const AmbientBackground = () => (
  <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
    {/* Subtle gradient orbs instead of 3D drones */}
    <div style={{ position: 'absolute', top: '10%', left: '5%', width: 400, height: 400, background: 'radial-gradient(circle, rgba(14,165,233,0.06) 0%, transparent 70%)', borderRadius: '50%' }} />
    <div style={{ position: 'absolute', top: '50%', right: '8%', width: 300, height: 300, background: 'radial-gradient(circle, rgba(230,81,0,0.05) 0%, transparent 70%)', borderRadius: '50%' }} />
    <div style={{ position: 'absolute', bottom: '15%', left: '15%', width: 350, height: 350, background: 'radial-gradient(circle, rgba(14,165,233,0.04) 0%, transparent 70%)', borderRadius: '50%' }} />
  </div>
);

const Home: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const heroRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      }
    }
  }, [location.hash]);

  useEffect(() => {
    document.title = "Flying Slap - Nasıl Katılabilirsiniz?";
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.title = "Flying Slap";
    if (heroRef.current && textRef.current) {
      gsap.to(textRef.current, {
        y: -100,
        opacity: 0,
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      });

      // Drone comes towards screen on scroll - Mobile Optimized
      const isMobile = window.innerWidth < 768;
      gsap.to('.drone-container', {
        scale: isMobile ? 1.4 : 1.8,
        y: isMobile ? 60 : 100,
        x: isMobile ? -20 : -50,
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      });
    }
  }, []);

  return (
    <div className="relative min-h-screen bg-white">
      {/* CSS-only ambient background */}
      <AmbientBackground />

      {/* Main Content Wrapper */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section
          id="gokyuzu-bizim"
          ref={heroRef}
          className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden text-white"
        >
          {/* Sky Blue CSS Background */}
          <div className="absolute inset-0 z-[-2] pointer-events-none overflow-hidden" style={{ background: 'linear-gradient(to bottom, #87CEEB, #ffffff)' }} />

          {/* Background Clouds - Disabled on mobile for performance */}
          {!isMobile && (
            <>
              <AnimatedCloud top="15%" delay={0} duration={40} opacity={0.4} scale={0.8} />
              <AnimatedCloud top="45%" delay={10} duration={55} opacity={0.3} scale={1.2} />
            </>
          )}

          <div className="relative h-full w-full flex items-center justify-center px-4 md:px-12 pt-24 md:pt-0">

            {/* Autonomous 3D Drone - Absolute Background */}
            <div className="absolute inset-0 z-0 pointer-events-none drone-container overflow-hidden">
              <Drone3D showMain={true} showBackground={false} />
            </div>

            {/* Foreground Text Content */}
            <div ref={textRef} className="relative z-10 text-center w-full max-w-4xl px-4 -mt-[8vh] sm:-mt-[10vh] md:mt-[10vh] lg:mt-[8vh]" style={{ pointerEvents: 'none' }}>

              {/* 1. EYEBROW: Future Plays Here Badge */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="flex flex-col items-center mb-8"
                style={{ pointerEvents: 'auto', paddingTop: window.innerWidth >= 768 ? '2rem' : '0' }}
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  style={{
                    position: 'relative',
                    background: 'rgba(255, 255, 255, 0.6)',
                    padding: '8px 24px',
                    borderRadius: '99px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.8)',
                    boxShadow: '0 4px 20px rgba(15, 45, 94, 0.08)',
                    backdropFilter: 'blur(12px)',
                    overflow: 'hidden'
                  }}
                >
                  <motion.div
                    animate={{ x: ['-100%', '200%'] }}
                    transition={{ repeat: Infinity, duration: 4, ease: 'linear' }}
                    style={{
                      position: 'absolute',
                      top: 0, left: 0, width: '80px', height: '100%',
                      background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.8), transparent)',
                      transform: 'skewX(-25deg)',
                      zIndex: 1
                    }}
                  />
                  <div className="w-1.5 h-1.5 rounded-full bg-[#e65100] relative z-10 mr-2" />
                  <span style={{
                    position: 'relative',
                    zIndex: 2,
                    color: '#0f2d5e',
                    fontSize: 'clamp(0.75rem, 1.5vw, 0.75rem)',
                    fontWeight: 900,
                    letterSpacing: '0.4em',
                    textTransform: 'uppercase',
                    marginRight: '-0.4em'
                  }}>
                    FUTURE PLAYS HERE
                  </span>
                </motion.div>
              </motion.div>

              {/* 2. BRAND LOGO */}
              <motion.div
                className="flex justify-center"
                style={{ marginBottom: '-1.5rem', pointerEvents: 'auto' }}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.1 }}
              >
                <img
                  src={logoImg}
                  alt="FlyingSlap Logo"
                  loading="eager"
                  fetchPriority="high"
                  decoding="sync"
                  style={{
                    width: 'clamp(145px, 20vw, 160px)',
                    height: 'auto',
                    objectFit: 'contain'
                  }}
                />
              </motion.div>

              {/* 3. BRAND TITLE */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                style={{ pointerEvents: 'auto' }}
              >
                <h1 className="text-[4rem] sm:text-[4.5rem] md:text-[4.5rem] lg:text-[5rem] font-black mb-6 lg:mb-6 tracking-tighter uppercase font-headline leading-[0.9] drop-shadow-2xl">
                  <span style={{ color: '#0f2d5e' }}>Flying</span><span style={{ color: '#e65100' }}>Slap</span>
                </h1>
              </motion.div>

              {/* 4. MANIFESTO SLOGAN */}
              <div className="flex flex-col items-center mb-8 px-2 relative lg:mb-6" style={{ pointerEvents: 'auto' }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  whileHover={{ scale: 1.01 }}
                  className="px-5 md:px-7 py-3"
                  style={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    borderRadius: '99px',
                    boxShadow: '0 4px 24px rgba(15, 45, 94, 0.04)',
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  <motion.div
                    animate={{ x: ['-100%', '200%'] }}
                    transition={{ repeat: Infinity, duration: 8, ease: 'linear', repeatDelay: 2 }}
                    style={{
                      position: 'absolute',
                      top: 0, left: 0, width: '100px', height: '100%',
                      background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.2), transparent)',
                      transform: 'skewX(-25deg)',
                      zIndex: 1
                    }}
                  />
                  <h2 className="font-headline tracking-tighter text-center relative z-10" style={{ lineHeight: 1.15 }}>
                    <span style={{
                      display: 'block',
                      fontSize: 'clamp(0.7rem, 1.2vw, 0.8rem)',
                      fontWeight: 500,
                      color: '#0f2d5e',
                      letterSpacing: '0.25em',
                      textTransform: 'uppercase',
                      marginBottom: '2px'
                    }}>
                      “&nbsp;Gökyüzüne herkes bakar
                    </span>
                    <span style={{
                      display: 'block',
                      fontSize: 'clamp(1rem, 1.8vw, 1.2rem)',
                      fontWeight: 900,
                      color: '#0f2d5e',
                      textTransform: 'uppercase',
                      letterSpacing: '-0.02em'
                    }}>
                      AMA SADECE <span style={{ color: '#e65100' }}>CESURLAR</span> <span style={{ whiteSpace: 'nowrap' }}>ÇIKAR.&nbsp;”</span>
                    </span>
                  </h2>
                </motion.div>
              </div>

              {/* 5. CTA BUTTON */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="flex justify-center"
                style={{ pointerEvents: 'auto' }}
              >
                <GlassButton size="default" contentClassName="group" onClick={() => navigate('/iletisim')}>
                  ARAMIZA KATIL <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </GlassButton>
              </motion.div>
            </div>
          </div>
        </section>
        
        {/* Kurtköy Pendik Drone Futbol Ligi Başarı & Görsel Slider */}
        <SuccessSlider />

        {/* Featured Section */}
        <FeaturedSection />

        {/* Contact Strip Section */}
        <section id="iletisim-strip" className="relative w-full overflow-hidden border-t border-black/5" style={{ minHeight: isMobile ? 140 : 160, marginTop: isMobile ? '60px' : '0' }}>
          {/* Shader Animation Background */}
          <ShaderAnimation />

          <div className={`relative z-10 max-w-7xl mx-auto px-8 md:px-16 w-full`} style={{ paddingTop: isMobile ? '48px' : '64px', paddingBottom: isMobile ? '48px' : '64px' }}>
            <div className={`flex flex-col md:flex-row items-center justify-between gap-8 ${isMobile ? 'text-center' : 'text-left'}`}>

              {/* Left: Text */}
              <div className={`flex flex-col gap-2 ${isMobile ? 'items-center text-center' : 'items-start text-left'}`}>
                <h3 className={`font-black font-headline tracking-tighter leading-[0.88] ${isMobile ? '' : 'text-3xl md:text-[40px]'}`}
                  style={{ fontSize: isMobile ? '28px' : undefined }}>
                  <span style={{ color: '#e65100' }}>BİZİMLE</span>{" "}
                  <span style={{ color: '#0f2d5e' }}>İLETİŞİME GEÇİN</span>
                </h3>
                <p className={`font-body ${isMobile ? 'text-sm' : 'text-base'}`} style={{ color: '#0f2d5e', opacity: 0.9, fontWeight: 600 }}>
                  Drone kurslarımız hakkında bilgi almak için bizimle iletişime geçin.
                </p>
              </div>

              {/* Right: Button */}
              <div className={isMobile ? "scale-90 origin-center" : ""}>
                <GlassButton
                  size={isMobile ? "sm" : "default"}
                  contentClassName="group"
                  onClick={() => navigate('/iletisim')}
                >
                  İLETİŞİM <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </GlassButton>
              </div>

            </div>
          </div>
        </section>

      </div> {/* End Main Content Wrapper */}
    </div>
  );
};

export default Home;
